/**
 * Reveal full patient ID for UI (decrypt `pid:v2:`) with a process-wide cache.
 * Legacy `pid:v1:` rows stay masked until re-entered.
 */
import { useEffect, useState } from 'react';
import {
  decryptPatientId,
  formatStoredPatientId,
  isPatientIdEncrypted,
} from '@/lib/patient-id';
import { getPatientIdDek } from '@/lib/patient-id-dek';
import type { Patient } from '@/lib/types';

const revealCache = new Map<string, string>();

export function clearPatientIdRevealCache(): void {
  revealCache.clear();
}

export function usePatientIdLabel(paciente: Patient | undefined | null): string | null {
  const sealed = paciente?.historiaClinica?.trim() ?? '';
  const hint = paciente?.historiaClinicaHint;
  const [label, setLabel] = useState<string | null>(() =>
    formatStoredPatientId(paciente ?? {}, {
      plaintext: sealed ? revealCache.get(sealed) : undefined,
    }),
  );

  useEffect(() => {
    if (!paciente || !sealed) {
      setLabel(null);
      return;
    }
    const cached = revealCache.get(sealed);
    if (cached) {
      setLabel(cached);
      return;
    }
    if (!isPatientIdEncrypted(sealed)) {
      setLabel(formatStoredPatientId(paciente));
      return;
    }
    const dek = getPatientIdDek();
    if (!dek) {
      setLabel(formatStoredPatientId(paciente));
      return;
    }
    let cancelled = false;
    void decryptPatientId(sealed, dek).then((plain) => {
      if (cancelled) return;
      if (plain) {
        revealCache.set(sealed, plain);
        setLabel(plain);
      } else {
        setLabel(formatStoredPatientId({ historiaClinica: sealed, historiaClinicaHint: hint }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sealed, hint, paciente]);

  return label;
}
